/*! © 2026 AirX (airx.art). All rights reserved. Unauthorized copying, reproduction, or redistribution of this code is prohibited. Report infringement: info@airx.art */

(function () {
  'use strict';

  if (customElements.get('custom-text-personalizer')) return;

  const POSITION_CLASS = {
    'Bottom Left': 'is-bottom-left',
    'Bottom Middle': 'is-bottom-middle',
    'Bottom Right': 'is-bottom-right',
  };

  const motionOn = () =>
    window.gsap && document.documentElement.classList.contains('motion');

  class CustomTextPersonalizer extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-personalizer-text]');
      this.select = this.querySelector('[data-personalizer-position]');
      this.count = this.querySelector('[data-personalizer-count]');
      if (!this.input) return;

      this.root = this.closest('main-product') || document;
      this.stage = this.root.querySelector('[data-media-stage]');

      this.buildOverlay();

      this.input.addEventListener('input', () => this.onInput());
      if (this.select) {
        this.select.addEventListener('change', () => {
          this.applyPosition();
          this.syncGallery(this.input.value.trim().length > 0);
        });
      }

      if (this.input.value.trim()) {
        window.requestAnimationFrame(() => this.onInput());
      }

      this.onResize = this.debounce(() => this.anchor(), 150);
      window.addEventListener('resize', this.onResize);

      if (window.AirX) {
        this.onMediaChange = () => this.anchor();
        window.AirX.on('media:change', this.onMediaChange);
        window.AirX.on('variant:change', this.onMediaChange);
        window.AirX.on('media:mode', this.onMediaChange);
      }
    }

    disconnectedCallback() {
      clearTimeout(this._viewerTimer);
      window.removeEventListener('resize', this.onResize);
      if (window.AirX && this.onMediaChange) {
        window.AirX.off('media:change', this.onMediaChange);
        window.AirX.off('variant:change', this.onMediaChange);
        window.AirX.off('media:mode', this.onMediaChange);
      }
      if (this.overlay) this.overlay.remove();
    }

    debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), wait);
      };
    }

    buildOverlay() {
      if (!this.stage) return;
      this.overlay = document.createElement('div');
      this.overlay.className = 'custom-text-overlay is-bottom-right';
      this.overlay.setAttribute('aria-hidden', 'true');
      this.text = document.createElement('span');
      this.text.className = 'custom-text-overlay__text';
      this.overlay.appendChild(this.text);
      this.stage.appendChild(this.overlay);
    }

    onInput() {
      const value = this.input.value;
      const has = value.trim().length > 0;

      if (this.count) {
        this.count.textContent = `${value.length}/${this.input.maxLength}`;
      }

      if (this.select) this.select.disabled = !has;

      if (!this.overlay) return;
      this.text.textContent = value;
      const wasVisible = this.overlay.classList.contains('is-visible');
      this.overlay.classList.toggle('is-visible', has);
      if (has) {
        this.anchor();
        if (!wasVisible && motionOn()) {
          window.gsap.fromTo(
            this.text,
            { opacity: 0, y: 6 },
            { opacity: 1, y: 0, duration: 0.45, ease: 'power3.out', clearProps: 'opacity,transform' }
          );
        }
      }
      this.syncGallery(has);
    }

    syncGallery(active) {
      const gallery = this.root.querySelector('product-media-gallery');
      if (!gallery) return;
      if (typeof gallery.setTextLock === 'function') gallery.setTextLock(active);
      clearTimeout(this._viewerTimer);
      this._viewerTimer = setTimeout(() => {
        const viewer = gallery.viewer;
        if (viewer && typeof viewer.setCustomText === 'function') {
          viewer.setCustomText(this.input.value, this.select ? this.select.value : 'Bottom Right');
        }
      }, 250);
    }

    applyPosition() {
      if (!this.overlay) return;
      const cls = POSITION_CLASS[this.select ? this.select.value : 'Bottom Right'] || 'is-bottom-right';
      this.overlay.classList.remove('is-bottom-left', 'is-bottom-middle', 'is-bottom-right');
      this.overlay.classList.add(cls);
    }

    activeImage() {
      if (!this.stage) return null;
      const slide = this.stage.querySelector('.product-gallery__slide.is-active');
      return slide ? slide.querySelector('img') : null;
    }

    anchor() {
      if (!this.overlay || !this.overlay.classList.contains('is-visible')) return;
      const img = this.activeImage();
      if (!img) return;

      const position = () => {
        const stageRect = this.stage.getBoundingClientRect();
        const rect = img.getBoundingClientRect();
        if (!rect.width) return;
        this.overlay.style.left = rect.left - stageRect.left + 'px';
        this.overlay.style.top = rect.top - stageRect.top + 'px';
        this.overlay.style.width = rect.width + 'px';
        this.overlay.style.height = rect.height + 'px';
        this.fitText(rect.width);
        this.applyPosition();
      };

      if (img.complete) {
        position();
      } else {
        img.addEventListener('load', position, { once: true });
      }
    }

    fitText(imgWidth) {
      const value = this.input.value.trim();
      if (!value) return;

      const base = imgWidth * 0.055;
      const crowding = Math.min(1, 16 / Math.max(value.length, 16));
      const px = Math.max(12, Math.min(44, base * (0.6 + 0.4 * crowding * 2)));
      this.text.style.fontSize = px + 'px';
    }
  }

  customElements.define('custom-text-personalizer', CustomTextPersonalizer);
})();
